class CartDrawerSection extends HTMLElement {
  cartUpdateUnsubscriber = undefined;
  constructor() {
    super();

    this.cartType = this.dataset.cartType;
    this.drawerClass = "wt-cart__drawer";
    this.drawer = this.querySelector(`.${this.drawerClass}`);
    this.classDrawerActive = `${this.drawerClass}--open`;
    this.pageOverlayClass = "page-overlay-cart";
    this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;
    this.body = document.body;
    this.triggerQuery = [
      `.wt-cart__trigger`,
      `.wt-cart__back-link`,
      `.${this.pageOverlayClass}`,
    ].join(", ");
    this.triggers = () => document.querySelectorAll(this.triggerQuery);
    this.isOpen = false;
    this.isCartPage = window.location.pathname === window.routes.cart_url;
    this.closeButton = () => this.querySelector(".wt-cart__drawer__close");
    this.mainTrigger = document.querySelector(".wt-cart__trigger");
    this.toggleEelements = () => this.querySelectorAll(this.dataset.toggleTabindex);

    this.giftWrapHandle = window.GIFT_WRAP_HANDLE;
    this.giftWrapVariantId = window.GIFT_WRAP_VARIANT_ID;
  }

  connectedCallback() {
    if (this.cartType === "page" || this.isCartPage) {
      document.addEventListener("cart-drawer:refresh", (e) => this.refreshCartDrawer(e));
      this.initializeGiftWrapCheckbox();
      return;
    }

    this.init();
    this.initializeGiftWrapCheckbox();

    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      if (this.isOpen) {
        setTabindex(this.toggleEelements(), "0");
        this.closeButton().focus();
      }
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
  }

  async getCart() {
    const res = await fetch("/cart.js");
    if (!res.ok) throw new Error("Failed to fetch cart");
    return res.json();
  }

  initializeGiftWrapCheckbox() {
    requestAnimationFrame(async () => {
      const checkbox = document.getElementById("cart_additional_checkbox");
      if (!checkbox) return;
      
      checkbox.checked = false;

      await this.syncGiftWrapCheckbox(checkbox);

      if (checkbox.dataset.bound === "true") return;
      checkbox.dataset.bound = "true";

      checkbox.addEventListener("change", async (e) => {
        try {
          if (e.target.checked) {
            await this.addGiftWrap();
          } else {
            await this.removeGiftWrap();
          }

          document.dispatchEvent(new CustomEvent("cart-drawer:refresh"));

          if (window.location.pathname === "/cart") {
            window.location.reload();
          }
        } catch (err) {
          console.error("Gift wrap update failed:", err);
        }
      });
    });
  }

  async syncGiftWrapCheckbox(checkbox) {
    try {
      const cart = await this.getCart();
      const hasGiftWrap = cart.items.some((i) => i.handle === this.giftWrapHandle);
      checkbox.checked = hasGiftWrap;
    } catch (err) {
      console.warn("Could not sync gift-wrap checkbox", err);
    }
  }

  async addGiftWrap() {
    const res = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: this.giftWrapVariantId, quantity: 1 }),
    });
    if (!res.ok) throw new Error("Failed to add gift wrap");
  }

  async removeGiftWrap() {
    const cart = await this.getCart();
    const item = cart.items.find((i) => i.handle === this.giftWrapHandle);
    if (!item) return;

    const res = await fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: item.key, quantity: 0 }),
    });
    if (!res.ok) throw new Error("Failed to remove gift wrap");
  }

  getFocusableElements() {
    const focusableElementsSelector = "button, [href], input, select, [tabindex]";
    const focusableElements = () =>
      Array.from(this.querySelectorAll(focusableElementsSelector)).filter(
        (el) => !el.hasAttribute("disabled") && el.tabIndex >= 0,
      );

    return {
      focusableElements,
      first: focusableElements()[0],
      last: focusableElements()[focusableElements().length - 1],
    };
  }

  temporaryHideFocusVisible() {
    document.body.classList.add("no-focus-visible");
  }

  onToggle() {
    if (this.hasAttribute("open")) {
      this.removeAttribute("open");
      this.isOpen = false;
      this.mainTrigger.focus();
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "-1");
    } else {
      this.setAttribute("open", "");
      this.isOpen = true;
      this.closeButton().focus();
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "0");
    }
  }

  toggleDrawerClasses() {
    this.onToggle();
    this.drawer.classList.toggle(this.classDrawerActive);
    this.body.classList.toggle(this.activeOverlayBodyClass);
  }

  init() {
    this.addEventListener("keydown", (e) => {
      const isTabPressed = e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
      const { first, last } = this.getFocusableElements();

      if (e.key === "Escape" || e.keyCode === 27 || e.code === "Escape") {
        if (this.isOpen) this.toggleDrawerClasses();
      }

      if (isTabPressed) {
        if (e.shiftKey && document.activeElement === first) {
          last.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    });

    this.attachTriggerListeners();

    this.addEventListener("click", (e) => {
      const closeButton = e.target.closest(".wt-cart__drawer__close");
      if (!closeButton) return;

      e.preventDefault();
      e.stopPropagation();
      if (this.isOpen) this.toggleDrawerClasses();
    });

    document.addEventListener("cart-drawer:refresh", (e) => this.refreshCartDrawer(e));
  }

  renderContents(parsedState, isClosedCart = true) {
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = document.querySelector(section.selector);
      if (!sectionElement) return;

      if (section.id === 'cart-icon-bubble') {
        sectionElement.outerHTML = parsedState.sections[section.id];
      } else {
        const newInner = this.getSectionInnerHTML(
          parsedState.sections[section.id],
          ".wt-cart__drawer__inner"
        );
        sectionElement.innerHTML = newInner;
      }
    });

    this.attachTriggerListeners();
    this.initializeGiftWrapCheckbox();

    const drawerItemsEl = document.querySelector("cart-drawer-items");
    if (drawerItemsEl) window.cartItems = drawerItemsEl;

    if (isClosedCart) {
      setTimeout(() => {
        this.toggleDrawerClasses();
        if (this.isOpen) this.closeButton()?.focus();
      });
    }
  }


  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer .wt-cart__drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        selector: "#cart-icon-bubble"
      },
    ];
  }

  refreshCartDrawer() {
    const sectionsToRender = this.getSectionsToRender();
    fetch(
      `${window.Shopify.routes.root}?sections=${sectionsToRender[0].id},${sectionsToRender[1].id}`,
    )
      .then((response) => response.json())
      .then((response) => {
        const parsedState = { sections: response };
        this.renderContents(parsedState, false);
      })
      .catch((e) => console.error(e));
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  openWithSkeleton(productInfo) {
    this.hasSkeletonActive = true;
    
    const skeletonHTML = this.createSkeletonItem(productInfo);
    
    const cartContents = this.querySelector('#CartDrawer-CartItems');
    if (!cartContents) return;
    
    let cartList = this.querySelector('.wt-cart__list');
    
    if (!cartList) {
      cartContents.innerHTML = `
        <ul class="wt-cart__list"></ul>
        <p id="CartDrawer-LiveRegionText" class="visually-hidden" role="status"></p>
        <p id="CartDrawer-LineItemStatus" class="visually-hidden" aria-hidden="true" role="status">Loading...</p>
      `;
      cartList = this.querySelector('.wt-cart__list');
    }
    
    cartList.insertAdjacentHTML('afterbegin', skeletonHTML);
    
    if (!this.isOpen) {
      this.toggleDrawerClasses();
    }
    
    this.drawer.classList.add('wt-cart__drawer--loading');
  }

  createSkeletonItem(productInfo) {
    return `
      <li class="cart-item wt-cart__item wt-cart__item--skeleton" data-skeleton-item>
        <picture class="wt-cart__item__thumb">
          ${productInfo.image ? 
            `<img src="${productInfo.image}" alt="${productInfo.name}" class="card__img wt-cart__item__img--loading">` :
            '<div class="card__img--placeholder skeleton-pulse"></div>'
          }
        </picture>
        <div class="wt-cart__item__body">
          <div class="wt-cart__item__body__wrapper">
            <div class="wt-cart__item__data">
              <h2 class="wt-cart__item__name cart-item__name">
                <span class="wt-cart__item__link">${productInfo.name}</span>
              </h2>
              <div class="options">
                ${productInfo.variant ? `<div class="product-option skeleton-shimmer"><span class="label">Variant: </span><span class="value">${productInfo.variant}</span></div>` : ''}
              </div>
              <div class="wt-cart__item__status">
                <span class="wt-cart__item__status-text">Adding to cart...</span>
              </div>
            </div>
          </div>
          <div class="wt-cart__item__price-container">
            <div class="wt-cart__item__price-wrapper">
              <span class="price price--end skeleton-shimmer">${productInfo.price}</span>
            </div>
            <div class="wt-cart__item__amount-wrapper">
              <div class="counter-wrapper">
                <div class="counter">
                  <span class="wt-cart__item__quantity-text">Qty: ${productInfo.quantity}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </li>
    `;
  }

  updateFromSkeleton(response) {
    const skeletonItem = this.querySelector('[data-skeleton-item]');
    if (skeletonItem) {
      skeletonItem.remove();
    }
    
    this.renderContents(response, false);
    
    this.drawer.classList.remove('wt-cart__drawer--loading');
    this.hasSkeletonActive = false;
  }

  closeOnError(errorMessage) {
    const skeletonItem = this.querySelector('[data-skeleton-item]');
    if (skeletonItem) {
      skeletonItem.classList.add('wt-cart__item--error');
      setTimeout(() => {
        skeletonItem.remove();
      }, 300);
    }
    
    this.drawer.classList.remove('wt-cart__drawer--loading');
    this.hasSkeletonActive = false;
    
    const cartList = this.querySelector('.wt-cart__list');
    if (cartList && cartList.children.length === 0) {
      this.toggleDrawerClasses();
    }
    
    console.error('Error adding product:', errorMessage);
  }

  attachTriggerListeners() {
    this.triggers().forEach((trigger) => {
      const clone = trigger.cloneNode(true);
      trigger.replaceWith(clone);
    });

    this.triggers().forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.toggleDrawerClasses();
      });
    });
  }
}

customElements.define("cart-drawer", CartDrawerSection);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      { id: "CartDrawer", section: "cart-drawer", selector: ".drawer__inner" },
      { id: "cart-icon-bubble", section: "cart-icon-bubble", selector: ".shopify-section" },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);
